import { type StreamingStateManager } from '@databases/database-ai-first-creation-state';
import { logError } from '@databases/logs';
import type {
  CsvErrorCollector,
  MetaToken,
  Token,
  ValueToken,
} from '@databases/streaming-csv-parser';
import { TokenType } from '@databases/streaming-csv-parser';

import type { DatabaseAgent } from '../agents/database-agent';
import type { ProcessingError } from '../types';

import { type EntryProcessor } from './entry-processor';
import type { FieldProcessor } from './field-processor';
import type { ValueProcessor } from './value-processor';
import type { ViewProcessor } from './view-processor';

export class DatabasesAIFCProcessor {
  constructor(
    private readonly agent: DatabaseAgent,
    private readonly streamingState: StreamingStateManager,
    private readonly valueProcessor: ValueProcessor,
    private readonly fieldProcessor: FieldProcessor,
    private readonly entryProcessor: EntryProcessor,
    private readonly viewProcessor: ViewProcessor,
    private readonly errorCollector?: CsvErrorCollector
  ) {}

  public onToken(token: Token) {
    switch (token.tokenType) {
      case TokenType.FIELD:
        this.fieldProcessor.handleFieldToken(token);
        break;
      case TokenType.VIEW:
        this.streamingState.markFieldsComplete();
        this.viewProcessor.handleViewToken(token);
        break;
      case TokenType.LAYOUT:
        this.viewProcessor.handleLayoutToken(token);
        break;
      case TokenType.ADD_FILTER:
        this.viewProcessor.handleFilterToken(token).catch((error) => {
          logError(error);
        });
        break;
      case TokenType.EDIT_FILTER:
        this.viewProcessor.handleFilterToken(token).catch((error) => {
          logError(error);
        });
        break;
      case TokenType.SORT:
        this.viewProcessor.handleSortToken(token);
        break;
      case TokenType.ADD_ENTRY:
        this.entryProcessor.handleAddEntryToken();
        break;
      case TokenType.EDIT_ENTRY:
        this.entryProcessor.handleEditEntryToken(token);
        break;
      case TokenType.REMOVE_ENTRY:
        this.entryProcessor.handleRemoveEntryToken(token);
        break;
      case TokenType.VALUE:
        this.handleValueToken(token);
        break;
      case TokenType.META:
        // dead code, not implemented
        this.handleMetaToken(token);
        break;
      case TokenType.EDIT_FIELD_SETTING:
        this.fieldProcessor.handleEditFieldSettingToken(token);
        break;
      case TokenType.EDIT_SORT:
        this.viewProcessor.handleEditSortToken(token);
        break;
      case TokenType.REMOVE_SORT:
        this.viewProcessor.handleRemoveSortToken(token);
        break;
      case TokenType.EDIT_VIEW_SETTING:
        this.viewProcessor.handleEditViewSettingToken(token);
        break;
      case TokenType.REMOVE_FIELD:
        this.fieldProcessor.handleRemoveFieldToken(token);
        break;
      case TokenType.REMOVE_VIEW:
        this.viewProcessor.handleRemoveViewToken(token);
        break;
      case TokenType.REMOVE_FILTER:
        this.viewProcessor.handleRemoveFilterToken(token);
        break;
      case TokenType.ADD_HIDDEN_FIELD:
        this.viewProcessor.handleAddHiddenFieldToken(token);
        break;
      case TokenType.REMOVE_HIDDEN_FIELD:
        this.viewProcessor.handleRemoveHiddenFieldToken(token);
        break;
      default:
        throw new Error(`Unknown token type: ${token}`);
    }
  }

  public clearDatabase(): void {
    this.agent.clearDatabase();
  }

  public onStreamingEnd(): void {
    this.valueProcessor.clearCurrentEntryId();
    this.valueProcessor.clearCurrentFieldId();
    this.streamingState.resetOnStreamingEnd();
  }

  private handleValueToken(valueToken: ValueToken) {
    this.valueProcessor.processValueToken(valueToken);
  }

  private handleMetaToken(metaToken: MetaToken) {
    const errors = this.validateMeta(metaToken);
    if (errors.length === 0) {
      // TODO handle meta token
      // make yjs mutation
    }
  }

  private validateMeta(_metaToken: MetaToken): ProcessingError[] {
    const errors: ProcessingError[] = [];
    // TODO validate meta
    return errors;
  }
}
